import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard, Loading } from "@/components/common";
import { Card } from "@/components/ui/card";
import { ClipboardList, Building2, Globe, Store, FileText, CheckCircle2, XCircle, CalendarDays, Boxes } from "lucide-react";

const RANGES = [{ v: "today", l: "Hari Ini" }, { v: "7d", l: "7 Hari" }, { v: "30d", l: "30 Hari" }, { v: "custom", l: "Custom" }];

function Breakdown({ title, rows, keyField, testId }) {
  return (
    <Card className="p-4" data-testid={testId}>
      <p className="mb-3 text-sm font-semibold text-slate-700">{title}</p>
      {(!rows || rows.length === 0) ? (
        <p className="text-xs text-slate-400">Belum ada data.</p>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center justify-between text-sm">
              <span className="text-slate-600">{r[keyField]}</span>
              <span className="font-semibold text-slate-900">{r.count}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function DpDashboard() {
  const [data, setData] = useState(null);
  const [ops, setOps] = useState(null);
  const [range, setRange] = useState("today");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [mat, setMat] = useState(null);

  useEffect(() => {
    api.get("/digital-printing/dashboard").then((r) => setData(r.data)).catch(() => setData(null));
    api.get("/digital-printing/material/dashboard").then((r) => setMat(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    const params = range === "custom" ? { range, date_from: from, date_to: to } : { range };
    api.get("/digital-printing/production/dashboard", { params }).then((r) => setOps(r.data)).catch(() => setOps(null));
  }, [range, from, to]);

  if (!data) return <Loading label="Memuat dashboard Digital Printing..." />;

  return (
    <div data-testid="dp-dashboard">
      <PageHeader title="Dashboard Digital Printing" subtitle="Monitoring Job & Produksi (read-only). Tidak menampilkan revenue/omzet/HPP/cost/material." />

      {ops && (
        <div className="mb-6" data-testid="dp-ops-section">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-slate-700">Operasional Produksi</span>
            <div className="ml-auto flex items-center gap-1.5">
              {RANGES.map((r) => (
                <button key={r.v} onClick={() => setRange(r.v)} data-testid={`dp-range-${r.v}`}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium ${range === r.v ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"}`}>{r.l}</button>
              ))}
              {range === "custom" && (
                <>
                  <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded border px-2 py-1 text-xs" data-testid="dp-range-from" />
                  <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded border px-2 py-1 text-xs" data-testid="dp-range-to" />
                </>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard label="Dalam Queue" value={ops.in_queue} icon={ClipboardList} accent="indigo" testId="dp-ops-queue" />
            <StatCard label="Printing" value={ops.printing} icon={FileText} accent="sky" testId="dp-ops-printing" />
            <StatCard label="Press" value={ops.press} icon={FileText} accent="violet" testId="dp-ops-press" />
            <StatCard label="Finishing" value={ops.finishing} icon={FileText} accent="amber" testId="dp-ops-finishing" />
            <StatCard label="Menunggu QC" value={ops.waiting_qc} icon={CheckCircle2} accent="amber" testId="dp-ops-waitingqc" />
            <StatCard label="Rework" value={ops.rework} icon={XCircle} accent="rose" testId="dp-ops-rework" />
            <StatCard label="Reject Qty" value={ops.reject_qty} icon={XCircle} accent="rose" testId="dp-ops-rejectqty" />
            <StatCard label="Job Terlambat" value={ops.overdue} icon={CalendarDays} accent="rose" testId="dp-ops-overdue" />
            <StatCard label="Completed (periode)" value={ops.completed_today} icon={CheckCircle2} accent="emerald" testId="dp-ops-completed" />
            <StatCard label="Internal (periode)" value={ops.internal_job} icon={Building2} accent="violet" testId="dp-ops-internal" />
            <StatCard label="Ext. Online (periode)" value={ops.external_online_job} icon={Globe} accent="sky" testId="dp-ops-online" />
            <StatCard label="Ext. Offline (periode)" value={ops.external_offline_job} icon={Store} accent="amber" testId="dp-ops-offline" />
          </div>
          <p className="mt-2 text-xs italic text-slate-400">{ops.note}</p>

          <p className="mb-2 mt-6 text-sm font-semibold text-slate-700">Hasil Produksi (output aktual · WIB)</p>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard label="Hasil Print DTF" value={`${(ops.output?.dtf_print_meter ?? 0).toFixed(2)} METER`} icon={FileText} accent="sky" testId="dp-output-dtf-meter" />
            <StatCard label="Hasil Press Kaos" value={`${Math.round(ops.output?.press_kaos_pcs ?? 0)} PCS`} icon={CheckCircle2} accent="violet" testId="dp-output-press-pcs" />
          </div>
          {ops.output?.by_operator?.length > 0 && (
            <div className="mt-3" data-testid="dp-output-by-operator">
              <p className="mb-1 text-xs font-medium text-slate-500">Output per operator</p>
              <div className="space-y-1">
                {ops.output.by_operator.map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-xs text-slate-600">
                    <span>{r.operator}</span>
                    <span className="font-semibold text-slate-900">{r.unit === "METER" ? r.output.toFixed(2) : Math.round(r.output)} {r.unit}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <p className="mb-2 text-sm font-semibold text-slate-700">Ringkasan Job</p>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Total Job" value={data.total_job} icon={ClipboardList} accent="indigo" testId="dp-stat-total" />
        <StatCard label="Job Internal" value={data.internal_job} icon={Building2} accent="violet" testId="dp-stat-internal" />
        <StatCard label="External Online" value={data.external_online_job} icon={Globe} accent="sky" testId="dp-stat-online" />
        <StatCard label="External Offline" value={data.external_offline_job} icon={Store} accent="amber" testId="dp-stat-offline" />
        <StatCard label="Draft" value={data.draft_job} icon={FileText} accent="slate" testId="dp-stat-draft" />
        <StatCard label="Open" value={data.open_job} icon={CheckCircle2} accent="emerald" testId="dp-stat-open" />
        <StatCard label="Cancelled" value={data.cancelled_job} icon={XCircle} accent="rose" testId="dp-stat-cancelled" />
        <StatCard label="Job Hari Ini" value={data.job_today} icon={CalendarDays} accent="indigo" testId="dp-stat-today" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Breakdown title="Berdasarkan Kategori Layanan" rows={data.by_category} keyField="category" testId="dp-bd-category" />
        <Breakdown title="Internal — Sumber / Toko" rows={data.by_internal_source} keyField="source" testId="dp-bd-source" />
        <Breakdown title="External — Channel" rows={data.by_external_channel} keyField="channel" testId="dp-bd-channel" />
      </div>

      <p className="mt-4 text-xs italic text-slate-400">{data.note}</p>

      {mat && (
        <div className="mt-6" data-testid="dp-material-section">
          <p className="mb-2 text-sm font-semibold text-slate-700">Material Operasional (DP3)</p>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard label="Material Issues Hari Ini" value={mat.material_issues_today} icon={Boxes} accent="indigo" testId="dp-mat-issues-today" />
            <StatCard label="Return Hari Ini" value={mat.returns_today} icon={Boxes} accent="sky" testId="dp-mat-returns-today" />
            <StatCard label="Waste Records" value={mat.waste_records} icon={XCircle} accent="rose" testId="dp-mat-waste-records" />
            <StatCard label="Job Belum Rekonsiliasi" value={(mat.jobs_unreconciled || []).length} icon={FileText} accent="amber" testId="dp-mat-unreconciled" />
          </div>
          <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Breakdown title="Top Waste Items" rows={mat.top_waste_items} keyField="item" testId="dp-mat-top-items" />
            <Breakdown title="Top Waste Reasons" rows={mat.top_waste_reasons} keyField="reason" testId="dp-mat-top-reasons" />
            <Card className="p-4" data-testid="dp-mat-shortage">
              <p className="mb-2 text-sm font-semibold text-slate-700">Job Material Shortage</p>
              {(mat.jobs_shortage || []).length === 0 ? <p className="text-xs text-slate-400">Tidak ada.</p> : mat.jobs_shortage.map((j, i) => <p key={i} className="text-xs text-rose-600">{j}</p>)}
            </Card>
          </div>
          <p className="mt-2 text-xs italic text-slate-400">{mat.note}</p>
        </div>
      )}
    </div>
  );
}
